import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  SelectChangeEvent,
  Collapse,
  IconButton,
} from '@mui/material';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import FournisseurAutoC from '../AutoComplete/FournisseurAutoC';
import GroupeBienAutoC from '../AutoComplete/GroupeBienAutoC';

import { GroupeBien, Tiers } from '../../models/models';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../reduxStore/Store';
import { resetFilters, setFilters } from '../../reduxStore/BienSlice';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { AppFilterResetButton } from '../layout/AppFilterGrid';


interface BienFiltersObjetct {
  nom: string | null;
  type: string | null;
  adresse: string | null;
  surface: number | null;
  proprietaire_id: number | null;
  groupe_bien_id: number | null;
  isDisponible: 0 | 1 | null;
  limit: number;
  offset: number;
}

const BienFilters = () => {

  const [showFilters, setShowFilters] = useState(false);

  const [resetKey, setResetKey] = useState(0);
  const dispatch = useDispatch<AppDispatch>();
  const { filters } = useSelector((state: any) => state.BienSlice);

  const [localNom, setLocalNom] = useState(filters.nom ?? '');
  const [localType, setLocalType] = useState(filters.type ?? '');
  const [localAdresse, setLocalAdresse] = useState(filters.adresse ?? '');
  const [localSurface, setLocalSurface] = useState(filters.surface ?? '');

  const nomSubject = useRef(new Subject<string>());
  const typeSubject = useRef(new Subject<string>());
  const adresseSubject = useRef(new Subject<string>());
  const surfaceSubject = useRef(new Subject<string>());

  useEffect(() => {
    const nomSub = nomSubject.current.pipe(debounceTime(500)).subscribe((value) => {
      dispatch(setFilters({ ...filters, nom: value }));
    });

    const typeSub = typeSubject.current.pipe(debounceTime(500)).subscribe((value) => {
      dispatch(setFilters({ ...filters, type: value }));
    });

    const adresseSub = adresseSubject.current.pipe(debounceTime(500)).subscribe((value) => {
      dispatch(setFilters({ ...filters, adresse: value }));
    });

    const surfaceSub = surfaceSubject.current.pipe(debounceTime(500)).subscribe((value) => {
      const numberValue = value === '' ? null : Number(value);
      dispatch(setFilters({ ...filters, surface: numberValue }));
    });

    return () => {
      nomSub.unsubscribe();
      typeSub.unsubscribe();
      adresseSub.unsubscribe();
      surfaceSub.unsubscribe();
    };
  }, [filters, dispatch]);

  useEffect(() => {
    setLocalNom(filters.nom ?? '');
    setLocalType(filters.type ?? '');
    setLocalAdresse(filters.adresse ?? '');
    setLocalSurface(filters.surface ?? '');
  }, [filters.nom, filters.type, filters.adresse, filters.surface]);

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const name = e.target.name as keyof BienFiltersObjetct;
    let value: string | null = e.target.value;

    if (value === 'null') value = null;

    if (name === 'isDisponible' && value !== null) {
      value = Number(value) as any;
    }

    dispatch(setFilters({ ...filters, [name]: value }));
  };

  const handleProprietaireChange = (proprietaire: Tiers | null) => {
    dispatch(setFilters({
      ...filters,
      proprietaire_id: proprietaire ? proprietaire.id : null,
    }));
  };

  const handleGroupeBienChange = (groupeBien: GroupeBien | null) => {
    dispatch(setFilters({
      ...filters,
      groupe_bien_id: groupeBien ? groupeBien.id : null,
    }));
  };

  const handleReset = () => {
    dispatch(resetFilters());
    setResetKey((prev) => prev + 1);
  };

  return (
    <div>
      {/* Toggle Button - OUTSIDE the Collapse */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <IconButton
          onClick={() => setShowFilters((prev) => !prev)}
          sx={{
            transform: showFilters ? 'rotate(0deg)' : 'rotate(180deg)',
            transition: 'transform 0.3s ease',
          }}
        >
          <KeyboardArrowDownIcon />
        </IconButton>
        <span>{"Filtres"}</span>
      </Box>

      {/* Collapsible Content */}
      <Collapse in={showFilters}>
        <Box
          component="form"
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 200px), 1fr))',
            alignItems: 'center',
            gap: 2,
            p: 2,
            bgcolor: 'background.paper',
            borderRadius: 1,
            boxShadow: 2,
          }}
        >
          <TextField
            label="Biens"
            name="nom"
            value={localNom}
            onChange={(e) => {
              setLocalNom(e.target.value);
              nomSubject.current.next(e.target.value);
            }}
            size="small"
           
          />

          <TextField
            label="Type"
            name="type"
            value={localType}
            onChange={(e) => {
              setLocalType(e.target.value);
              typeSubject.current.next(e.target.value);
            }}
            size="small"
           
          />

          <TextField
            label="Adresse"
            name="adresse"
            value={localAdresse}
            onChange={(e) => {
              setLocalAdresse(e.target.value);
              adresseSubject.current.next(e.target.value);
            }}
            size="small"
           
          />

          <TextField
            label="Surface (m²)"
            name="surface"
            type="number"
            value={localSurface}
            onChange={(e) => {
              setLocalSurface(e.target.value);
              surfaceSubject.current.next(e.target.value);
            }}
            size="small"
           
            inputProps={{ min: 0 }}
          />

          <FournisseurAutoC
            onFournisseurChange={handleProprietaireChange}
            key={`proprietaire${resetKey}`}
          />

          <GroupeBienAutoC
            key={`groupebien${resetKey}`}
            onGroupeBienChange={handleGroupeBienChange}
          />

          <FormControl size="small">
            <InputLabel id="disponible-label">Disponible</InputLabel>
            <Select
              labelId="disponible-label"
              label="Disponible"
              name="isDisponible"
              value={
                filters.isDisponible === null
                  ? 'null'
                  : filters.isDisponible.toString()
              }
              onChange={handleSelectChange}
            >
              <MenuItem value="null">Tous</MenuItem>
              <MenuItem value="1">Disponible</MenuItem>
              <MenuItem value="0">Non Disponible</MenuItem>
            </Select>
          </FormControl>

          <AppFilterResetButton onClick={handleReset} />
        </Box>
      </Collapse>
    </div>
  );

};

export default BienFilters;
